import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, PlayCircle } from "lucide-react";
import { HERO_IMAGE } from "@/lib/siteData";
import LeadDialog from "@/components/site/LeadDialog";
import MagneticButton from "@/components/site/MagneticButton";
import Particles from "@/components/site/Particles";
import { RevealLines, RevealWords } from "@/components/site/Reveal";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
    const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.55]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero"
            className="relative h-[100svh] min-h-[680px] w-full overflow-hidden"
        >
            {/* Parallax bg */}
            <motion.div
                aria-hidden
                style={{ y: bgY, scale: bgScale, backgroundImage: `url(${HERO_IMAGE})` }}
                className="absolute inset-0 will-change-transform bg-center bg-cover animate-kenburns"
            />

            {/* Overlays */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/92 via-black/60 to-black/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/30" />
            <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />

            {/* Particles overlay */}
            <Particles density={65} className="opacity-70" />

            {/* Grain */}
            <div className="absolute inset-0 grain pointer-events-none" />

            {/* Cinema bars */}
            <motion.div
                initial={{ scaleY: 1 }}
                animate={{ scaleY: 0 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.7, 0, 0.2, 1] }}
                className="absolute top-0 inset-x-0 h-20 bg-black origin-top z-20"
            />
            <motion.div
                initial={{ scaleY: 1 }}
                animate={{ scaleY: 0 }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.7, 0, 0.2, 1] }}
                className="absolute bottom-0 inset-x-0 h-20 bg-black origin-bottom z-20"
            />

            {/* Content */}
            <motion.div style={{ y: textY }} className="relative z-10 h-full flex items-end md:items-center">
                <div className="max-w-7xl mx-auto w-full px-5 md:px-8 pb-16 md:pb-0">
                    {/* Eyebrow */}
                    <div className="overflow-hidden mb-6">
                        <motion.div
                            initial={{ y: "120%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.9, delay: 0.5, ease: [0.19, 1, 0.22, 1] }}
                            className="flex items-center gap-3"
                        >
                            <span className="divider-gold" />
                            <span className="uppercase tracking-[0.32em] text-[11px] text-gold font-medium">
                                Architectural Visualization Studio · Est. 2024
                            </span>
                        </motion.div>
                    </div>

                    {/* Kinetic headline */}
                    <h1
                        data-testid="hero-headline"
                        className="font-display text-white text-[13vw] sm:text-6xl lg:text-8xl xl:text-[7.5rem] leading-[0.98] tracking-tight max-w-5xl"
                    >
                        <RevealLines
                            lines={["Experience your", <span key="l2"><span className="italic text-gold">dream</span> before</span>, "it's built."]}
                            stagger={0.14}
                            delay={0.75}
                        />
                    </h1>

                    {/* Sub */}
                    <div className="mt-8 max-w-2xl">
                        <RevealWords
                            text="We craft ultra-realistic 3D visualizations so you can walk your future home before a single brick is laid — with confidence, clarity, and zero costly regrets."
                            className="text-base md:text-lg text-white/80 leading-relaxed"
                            delay={1.35}
                            stagger={0.018}
                        />
                    </div>

                    {/* CTAs */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <LeadDialog
                            trigger={
                                <MagneticButton
                                    className="group inline-flex items-center gap-2 h-14 px-8 rounded-full bg-gold text-white font-medium hover:brightness-110 transition"
                                    data-testid="hero-primary-cta"
                                >
                                    Book a Free Consultation
                                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </MagneticButton>
                            }
                        />
                        <MagneticButton
                            as="a"
                            href="#portfolio"
                            className="inline-flex items-center gap-2 h-14 px-8 rounded-full border border-white/40 text-white hover:bg-white hover:text-black transition"
                            data-testid="hero-secondary-cta"
                        >
                            <PlayCircle className="h-4 w-4" />
                            View Portfolio
                        </MagneticButton>
                    </motion.div>

                    {/* Bottom meta */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2.3, duration: 0.6 }}
                        className="mt-16 hidden md:flex items-center gap-8 text-white/60 text-[11px] uppercase tracking-[0.28em]"
                    >
                        <span>Hyderabad · Telangana</span>
                        <span className="h-px w-16 bg-white/25" />
                        <span>Feel Before You Build</span>
                        <span className="h-px w-16 bg-white/25" />
                        <span>SketchUp · Lumion · V-Ray</span>
                    </motion.div>
                </div>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.6 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-3"
            >
                <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Scroll</span>
                <div className="h-10 w-6 border border-white/40 rounded-full grid place-items-center">
                    <span className="h-2 w-[2px] bg-white/60 animate-pulse" />
                </div>
            </motion.div>

            {/* Chapter tag right */}
            <div className="absolute right-6 top-1/2 -translate-y-1/2 z-10 hidden lg:flex flex-col items-center gap-4 text-white/40">
                <span className="text-[10px] uppercase tracking-[0.32em] -rotate-90">Chapter · 01</span>
                <span className="h-16 w-px bg-white/20" />
            </div>
        </section>
    );
}
